// #region imports

// --- Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

// --- RxJs
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// --- 3rd Party
import { retryBackoff } from 'backoff-rxjs';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { environment } from '../../../../environments/environment';
import { DemographicModel, ApplicationError, ErrorMessage } from '../../../models';
import { AppService } from '../app.service';
import { DemographicsBaseService } from './demographics-base.service';

// #endregion

@Injectable({
  providedIn: 'root'
})
export class DemographicsService extends DemographicsBaseService {
  constructor(
    private appService: AppService,
    private httpClient: HttpClient,
    private translate: TranslateService
  ) {
    super();
  }

  private _demographics: DemographicModel[] | undefined;

  loadDemographics(useCache: boolean): void {

    if (useCache && this._demographics && this._demographics.length > 0) {
      return;
    }

    const uri =
      environment.apiConfig.surveyApiUrl + environment.apiConfig.demographicsApi;
    this.appService.setLoading(true);
    this._demographicsLoading$.next(true);
    this.httpClient
      .get<DemographicModel[]>(uri)
      .pipe(
        map(response => {
          const mapped = response.map(
            (data: DemographicModel) =>
              new DemographicModel(
                data.id,
                data.name,
                data.isMandatory,
                data.values,
                data.moniker
              )

          );
          return mapped;
        }),
        retryBackoff(environment.retryConfig),
        catchError(this.handleError.bind(this))
      )
      .subscribe((demographics: DemographicModel[] | null) => {
        if (!demographics) return;

        this._demographics = demographics;
        this._demographics$.next(demographics);
        this._demographicsLoading$.next(false);
        this.appService.setLoading(false);
      });
  }

  private handleError(err: any): Observable<DemographicModel[] | null> {
    this.appService.setLoading(false);

    const errorMessage: string = this.translate.instant('errorMessageGenericBackend');

    const error: ApplicationError = ApplicationError.create(
      ErrorMessage.genericErrorCode,
      'DemographicsService',
      true,
      err);

    this.appService.setLastError(error);

    return of(null);
  }
}
